import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { 
  TrendUp, 
  Clock, 
  ArrowRight,
  Buildings,
  Globe,
  Newspaper,
  CaretRight
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Country flags
const COUNTRY_FLAGS = {
  uk: "🇬🇧",
  ireland: "🇮🇪",
  usa: "🇺🇸"
};

// Exchange badges
const EXCHANGE_COLORS = {
  LSE: "bg-[#1E3A5F] text-white",
  Euronext: "bg-[#059669] text-white",
  NYSE: "bg-[#0F766E] text-white"
};

function StockNewsCard({ news, compact = false }) {
  const timeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) return `${diffDays}d ago`;
    if (diffHours > 0) return `${diffHours}h ago`;
    return "Just now";
  };

  if (compact) {
    return (
      <Link
        to={`/stock-news/${news.id}`}
        className="block p-4 border-b border-[#E0D4C8] hover:bg-[#F9FAFB] transition-colors group"
        data-testid={`stock-news-compact-${news.id}`}
      >
        <div className="flex items-start gap-3">
          <span className="text-lg">{COUNTRY_FLAGS[news.country] || "🌍"}</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase ${EXCHANGE_COLORS[news.exchange] || "bg-gray-500 text-white"}`}>
                {news.ticker}
              </span>
              <span className="text-xs text-[#6B6661]">{news.company}</span>
            </div>
            <h3 className="text-sm font-medium text-[#33302E] line-clamp-2 group-hover:text-[#0F766E] transition-colors">
              {news.headline}
            </h3>
          </div>
          <span className="text-xs text-[#8B8580] whitespace-nowrap">{timeAgo(news.published_at)}</span>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/stock-news/${news.id}`}
      className="block bg-white border border-[#E0D4C8] hover:border-[#0F766E] transition-all group"
      data-testid={`stock-news-card-${news.id}`}
    >
      <div className="p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-xl">{COUNTRY_FLAGS[news.country] || "🌍"}</span>
            <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${EXCHANGE_COLORS[news.exchange] || "bg-gray-500 text-white"}`}>
              {news.ticker}
            </span>
            <span className="text-sm text-[#6B6661]">{news.company}</span>
          </div>
          <div className="flex items-center gap-1 text-xs text-[#8B8580]">
            <Clock size={12} />
            {timeAgo(news.published_at)}
          </div>
        </div>

        {/* Headline */}
        <h3 className="font-serif text-lg text-[#33302E] mb-3 line-clamp-2 group-hover:text-[#0F766E] transition-colors">
          {news.headline}
        </h3>

        {/* Summary */}
        <p className="text-sm text-[#6B6661] line-clamp-3 mb-4">
          {news.summary}
        </p>

        {/* Footer */}
        <div className="flex items-center justify-between pt-3 border-t border-[#F3F4F6]">
          <span className="text-xs text-[#8B8580]">
            via {news.source}
          </span>
          <span className="text-xs text-[#0F766E] font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
            Read Full Story
            <ArrowRight size={12} />
          </span>
        </div>
      </div>
    </Link>
  );
}

export function StockNewsWidget({ limit = 5 }) {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNews = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/stock-news?limit=${limit}`);
      setNews(response.data);
    } catch (error) {
      console.error("Error fetching stock news:", error);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  if (loading) {
    return (
      <div className="bg-white border border-[#E0D4C8]">
        <div className="p-4 border-b border-[#E0D4C8] bg-gradient-to-r from-[#022C22] to-[#064E3B]">
          <div className="flex items-center gap-2 text-white">
            <TrendUp size={20} weight="bold" />
            <h3 className="font-serif text-lg">Agri Stock News</h3>
          </div>
        </div>
        <div className="p-8 text-center text-[#6B6661]">Loading...</div>
      </div>
    );
  }

  if (news.length === 0) {
    return (
      <div className="bg-white border border-[#E0D4C8]">
        <div className="p-4 border-b border-[#E0D4C8] bg-gradient-to-r from-[#022C22] to-[#064E3B]">
          <div className="flex items-center gap-2 text-white">
            <TrendUp size={20} weight="bold" />
            <h3 className="font-serif text-lg">Agri Stock News</h3>
          </div>
        </div>
        <div className="p-6 text-center">
          <Newspaper size={32} className="mx-auto text-[#D1D5DB] mb-3" />
          <p className="text-sm text-[#6B6661]">No stock news yet</p>
          <p className="text-xs text-[#8B8580] mt-1">News will appear here when available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#E0D4C8]">
      <div className="p-4 border-b border-[#E0D4C8] bg-gradient-to-r from-[#022C22] to-[#064E3B]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <TrendUp size={20} weight="bold" />
            <h3 className="font-serif text-lg">Agri Stock News</h3>
          </div>
          <Link 
            to="/stock-news"
            className="text-xs text-[#6EE7B7] hover:text-white flex items-center gap-1 transition-colors"
          >
            View All <CaretRight size={12} />
          </Link>
        </div>
      </div>
      <div>
        {news.map((item) => (
          <StockNewsCard key={item.id} news={item} compact />
        ))}
      </div>
    </div>
  );
}

export function StockNewsGrid({ news, loading }) {
  if (loading) {
    return (
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white border border-[#E0D4C8] p-5 animate-pulse">
            <div className="h-4 bg-[#E5E7EB] rounded w-1/3 mb-3" />
            <div className="h-6 bg-[#E5E7EB] rounded mb-3" />
            <div className="h-4 bg-[#E5E7EB] rounded mb-2" />
            <div className="h-4 bg-[#E5E7EB] rounded w-2/3" />
          </div>
        ))}
      </div>
    );
  }

  if (news.length === 0) {
    return (
      <div className="text-center py-12">
        <Newspaper size={48} className="mx-auto text-[#D1D5DB] mb-4" />
        <h3 className="font-serif text-xl text-[#374151] mb-2">No Stock News Yet</h3>
        <p className="text-[#6B6661]">
          Stock news from agriculture companies will appear here when available.
        </p>
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {news.map((item) => (
        <StockNewsCard key={item.id} news={item} />
      ))}
    </div>
  );
}

export default StockNewsCard;
